import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Calc, FileText, GraduationCap, Stethoscope, Phone, Check } from '../components/Icons.jsx';

const sections = [
  {
    n: 'I',
    name: 'Section 1 · Reasoning in Humanities & Social Sciences',
    duration: '70 minutes',
    questions: '47 MCQs',
    body: 'Comprehension and interpretation of written and visual material — passages, poems, cartoons. Tests your reading speed and your ability to extract argument structure under time pressure. Stylistically the closest of the three to UCAT VR.',
    tip: 'High-reading-volume habit beats any drill. Read Aeon, the LRB, The Atlantic. Pay attention to argument scaffolding, not just content.'
  },
  {
    n: 'II',
    name: 'Section 2 · Written Communication',
    duration: '60 minutes (2 essays)',
    questions: '2 essays',
    body: 'Two essays, each based on a set of 4–5 thematic quotes. Task A is typically social/cultural, Task B more personal/reflective. 30 minutes each. Markers want a clear thesis, structured argument and a recognisable voice.',
    tip: 'Practice essays under timer pressure — Medic Mind\'s essay tool below gives you fresh quote sets and a 30-min countdown. The biggest single lift is having a default structure you can deploy without thinking.'
  },
  {
    n: 'III',
    name: 'Section 3 · Reasoning in Biological & Physical Sciences',
    duration: '150 minutes',
    questions: '75 MCQs',
    body: 'Biology, chemistry and physics at first-year-undergraduate level — but more about reasoning under unfamiliar contexts than recalling facts. Most universities double-weight this section in the overall score, so it dominates outcomes.',
    tip: 'A-level chemistry and biology are baseline. Most candidates also need to brush up first-year physics (mechanics, electricity, waves). Time pressure is brutal — pace yourself at exactly 2 minutes per question.'
  }
];

const uniUsers = [
  { name: 'University of Cambridge (Graduate Course in Medicine)', notes: 'GAMSAT + interview + degree class. Very competitive.' },
  { name: 'University of Nottingham (GEM)', notes: 'GAMSAT + interview. Threshold-based.' },
  { name: 'Swansea University (Graduate Entry Medicine)', notes: 'GAMSAT + interview. Welsh applicants get priority allocations.' },
  { name: 'University of Liverpool (A101)', notes: 'GAMSAT + 2:1 minimum degree.' },
  { name: 'University of Plymouth (BMBS Graduate Entry)', notes: 'GAMSAT + interview.' },
  { name: 'Anglia Ruskin University (GEM)', notes: 'GAMSAT + interview + Section 3 minimum.' },
  { name: 'University of Sunderland (GEM)', notes: 'GAMSAT + 2:1 minimum + interview.' },
  { name: 'University of Worcester / Three Counties Medical School', notes: 'GAMSAT + interview.' },
  { name: 'University of Ulster (Graduate Entry Medicine)', notes: 'GAMSAT + interview.' }
];

export default function Gamsat() {
  return (
    <>
      <div className="page-head">
        <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Graduate-entry route</div>
        <h1>GAMSAT — the test for graduate-entry medicine</h1>
        <p className="subtitle">GAMSAT is the entrance test most UK graduate-entry medicine (A101 / GEM) courses use instead of UCAT. Three sections, sat twice a year (March and September), scored 1–100 per section. Section 3 is double-weighted at most universities — it's the section that decides outcomes.</p>
      </div>

      <section className="card" style={{
        background: 'linear-gradient(135deg, var(--feature-from), var(--feature-via), var(--feature-to))',
        color: '#fff', border: 'none', marginBottom: 24, padding: '26px 30px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ width: 52, height: 52, background: 'rgba(255,255,255,0.15)', borderRadius: 14, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <GraduationCap width="26" height="26" />
          </div>
          <div style={{ flex: 1, minWidth: 260 }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' }}>Medic Mind GAMSAT tutoring</h2>
            <p style={{ margin: 0, color: '#e9d5ff', maxWidth: 580 }}>1-to-1 tutoring across all three sections by tutors who've sat GAMSAT and scored in the top decile. Section 3 deep-dives, Section 2 structured-essay coaching, and full mock exams with detailed feedback.</p>
          </div>
          <a className="btn" href="https://www.medicmind.co.uk/contact-us/" target="_blank" rel="noreferrer" style={{ background: '#fff', color: 'var(--brand-deep)' }}>Book GAMSAT tutoring <ArrowRight width="16" height="16" /></a>
        </div>
      </section>

      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>The three sections</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 28 }}>
        {sections.map((s) => (
          <div key={s.n} className="card" style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 18, alignItems: 'flex-start' }}>
            <div style={{ background: 'var(--accent-soft)', color: 'var(--brand-purple)', borderRadius: 12, display: 'grid', placeItems: 'center', padding: '14px 0' }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em' }}>SEC</div>
              <div style={{ fontSize: 26, fontWeight: 700 }}>{s.n}</div>
            </div>
            <div>
              <h3 style={{ margin: '0 0 4px', fontSize: 17 }}>{s.name}</h3>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                <span style={chip}>{s.duration}</span>
                <span style={chip}>{s.questions}</span>
              </div>
              <p style={{ margin: '0 0 8px', color: 'var(--text-2)', fontSize: 14.5, lineHeight: 1.6 }}>{s.body}</p>
              <p style={{ margin: 0, fontSize: 13.5, padding: '8px 12px', background: 'var(--card-soft)', borderLeft: '3px solid var(--brand-purple)', borderRadius: 6 }}>
                <strong>How to attack it: </strong>{s.tip}
              </p>
            </div>
          </div>
        ))}
      </div>

      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>How the score works</h2>
      <div className="card" style={{ marginBottom: 24 }}>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10, color: 'var(--text-2)', fontSize: 14.5, lineHeight: 1.6 }}>
          <li style={li}><strong>Each section</strong> is scored 1–100, scaled so the median is around 50.</li>
          <li style={li}><strong>Overall score</strong> is a weighted average. Most UK universities use a 1:1:2 weighting — so Section 3 is worth double. A few use straight averaging.</li>
          <li style={li}><strong>Competitive overall scores</strong> at UK GEM schools start around <strong>63–67</strong>. Cambridge typically wants <strong>70+</strong>.</li>
          <li style={li}><strong>Section minimums</strong> apply at some unis (e.g. Anglia Ruskin) — even a strong overall won't get through if a single section is below the floor.</li>
          <li style={li}><strong>Scores are valid for 2 years</strong>. You can sit twice a year, so you can re-take without burning a cycle.</li>
        </ul>
      </div>

      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>UK universities that use GAMSAT</h2>
      <div className="card" style={{ padding: 0, marginBottom: 24, overflow: 'hidden' }}>
        <div className="table-scroll">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--card-soft)' }}>
              <th style={cellHead}>University</th>
              <th style={cellHead}>How they use GAMSAT</th>
            </tr>
          </thead>
          <tbody>
            {uniUsers.map((u) => (
              <tr key={u.name} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={cell}><strong>{u.name}</strong></td>
                <td style={cell}>{u.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>Free GAMSAT tools</h2>
      <div className="parents-grid" style={{ marginBottom: 24 }}>
        <Link to="/gamsat-essay" className="parents-card" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
          <div className="icon-wrap"><FileText /></div>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent)', fontWeight: 700, marginBottom: 4 }}>Section 2 timer</div>
          <h3>Essay practice — 30-min sprint</h3>
          <p style={{ color: 'var(--text-2)', fontSize: 14, margin: '8px 0 12px' }}>Fresh quote-set every run. Built-in 30-minute countdown matches the real GAMSAT pacing. Live word count + a structure overlay you can hide.</p>
          <span style={{ color: 'var(--accent)', fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>Open tool <ArrowRight width="14" height="14" /></span>
        </Link>

        <Link to="/medi" className="parents-card" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
          <div className="icon-wrap"><Brain /></div>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent)', fontWeight: 700, marginBottom: 4 }}>AI tutor</div>
          <h3>Ask Ash about GAMSAT</h3>
          <p style={{ color: 'var(--text-2)', fontSize: 14, margin: '8px 0 12px' }}>Score interpretation, section weightings per university, study plans, re-take advice. Session-only — no signup.</p>
          <span style={{ color: 'var(--accent)', fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>Chat with Ash <ArrowRight width="14" height="14" /></span>
        </Link>

        <Link to="/timeline" className="parents-card" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
          <div className="icon-wrap"><GraduationCap /></div>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent)', fontWeight: 700, marginBottom: 4 }}>Application timeline</div>
          <h3>Where GAMSAT fits</h3>
          <p style={{ color: 'var(--text-2)', fontSize: 14, margin: '8px 0 12px' }}>See the full graduate-entry route — degree class requirements, GAMSAT sitting choice, application deadlines, interview season.</p>
          <span style={{ color: 'var(--accent)', fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>See timeline <ArrowRight width="14" height="14" /></span>
        </Link>
      </div>

      <section className="parents-cta" style={{ marginTop: 12 }}>
        <div>
          <h2>Section 3 is where 80% of candidates lose marks.</h2>
          <p>1-to-1 GAMSAT tutoring with a Medic Mind tutor who scored in the top decile. Personalised plan after one diagnostic mock. Section 3 deep-dives, Section 2 essay coaching, full mock-exam feedback.</p>
        </div>
        <a className="btn" href="https://www.medicmind.co.uk/contact-us/" target="_blank" rel="noreferrer">Book a free GAMSAT consultation <ArrowRight width="16" height="16" /></a>
      </section>
    </>
  );
}

const chip = {
  padding: '3px 9px', background: 'var(--card-soft)', borderRadius: 99,
  border: '1px solid var(--border)', color: 'var(--text-2)', fontWeight: 600
};
const li = { display: 'flex', gap: 10, alignItems: 'flex-start' };
li[':before'] = '';
const cellHead = { padding: '12px 14px', textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 };
const cell = { padding: '12px 14px', fontSize: 14, verticalAlign: 'top' };
